import {
  NotificationChannelId,
  SubmitNoticeType
} from './subscription-dialog.models';

export interface SubscriptionSubmitFailureResolution {
  message: string;
  type: SubmitNoticeType;
}

export function resolveSubscriptionSubmitFailure(
  errorOrMessage: any,
  duplicateChannelLabel: string
): SubscriptionSubmitFailureResolution {
  const details = extractSubmitFailureDetails(errorOrMessage);
  const raw = details.message;

  if (isDuplicateSubscriptionMessage(raw)) {
    return {
      message: `You're already subscribed to this status page via ${duplicateChannelLabel}.`,
      type: 'info'
    };
  }

  if (!raw) {
    return {
      message: 'Unable to subscribe right now. Please try again.',
      type: 'error'
    };
  }

  return {
    message: raw,
    type: 'error'
  };
}

export function getChannelLabel(
  channels: ReadonlyArray<{ id: NotificationChannelId; label: string }>,
  channelId: NotificationChannelId
): string {
  const channel = channels.find((option) => option.id === channelId);
  return channel?.label || 'this channel';
}

function extractSubmitFailureDetails(errorOrMessage: any): { message: string; status: number | null } {
  if (typeof errorOrMessage === 'string') {
    const parsedPayload = parseSubmitFailurePayload(errorOrMessage);
    const parsedStatusCandidate = Number(parsedPayload?.statusCode ?? parsedPayload?.status);
    const parsedStatus = Number.isFinite(parsedStatusCandidate) ? parsedStatusCandidate : null;
    const parsedMessage = String(
      parsedPayload?.error?.errorMessage ||
      parsedPayload?.error?.message ||
      parsedPayload?.errorMessage ||
      parsedPayload?.message ||
      ''
    ).trim();

    return {
      message: parsedMessage || errorOrMessage.trim(),
      status: parsedStatus
    };
  }

  const parsedPayload = parseSubmitFailurePayload(errorOrMessage?.error);
  const statusCandidate = Number(
    errorOrMessage?.status ??
    parsedPayload?.statusCode ??
    parsedPayload?.status
  );
  const status = Number.isFinite(statusCandidate) ? statusCandidate : null;

  const messageCandidates = [
    parsedPayload?.error?.errorMessage,
    parsedPayload?.error?.message,
    parsedPayload?.errorMessage,
    parsedPayload?.message,
    errorOrMessage?.error?.errorMessage,
    errorOrMessage?.error?.message,
    errorOrMessage?.errorMessage,
    typeof errorOrMessage?.error === 'string' ? errorOrMessage.error : '',
    errorOrMessage?.message
  ]
    .map((value) => String(value ?? '').trim())
    .filter((value) => !!value);

  const rawMessage =
    messageCandidates.find((value) => !isHttpClientTransportFailureMessage(value)) ||
    messageCandidates[0] ||
    '';

  return {
    message: rawMessage,
    status
  };
}

function parseSubmitFailurePayload(payload: any): Record<string, any> | null {
  if (!payload) {
    return null;
  }
  if (typeof payload === 'object') {
    return payload as Record<string, any>;
  }
  if (typeof payload !== 'string') {
    return null;
  }

  const trimmed = payload.trim();
  if (!trimmed) {
    return null;
  }
  if (!((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? parsed as Record<string, any> : null;
  } catch {
    return null;
  }
}

function isHttpClientTransportFailureMessage(message: string): boolean {
  const normalized = String(message || '').trim().toLowerCase();
  return normalized.startsWith('http failure response for ');
}

function isDuplicateSubscriptionMessage(message: string): boolean {
  const normalized = String(message || '').trim().toLowerCase();
  return normalized.includes('already subscribed') ||
    normalized.includes('already exists') ||
    normalized.includes('duplicate');
}
